import { UcoastEl } from '@/scripts/core/UcoastEl'
import { motionEnabled } from '@/scripts/core/motion'

export class FaqAccordions extends UcoastEl {
	static htmlSelector = 'faq-accordions'

	private details: HTMLDetailsElement[] = []
	private animations = new Map<HTMLDetailsElement, Animation>()
	private targetStates = new Map<HTMLDetailsElement, boolean>()
	private summaryHandlers = new Map<HTMLDetailsElement, (event: MouseEvent) => void>()

	override connectedCallback(): void {
		super.connectedCallback()
		if (this.summaryHandlers.size > 0) return

		this.details = Array.from(
			this.querySelectorAll<HTMLDetailsElement>('.accordion__details')
		)

		let hasOpenDetail = false
		this.details.forEach((detail) => {
			if (!detail.open) return
			if (hasOpenDetail) detail.open = false
			hasOpenDetail = true
		})

		this.details.forEach((detail) => {
			this.targetStates.set(detail, detail.open)
			const summary = detail.querySelector('summary')

			if (!summary) return

			const handler = (event: MouseEvent) => {
				event.preventDefault()

				const isOpen = this.targetStates.get(detail) ?? detail.open
				this.setOpen(detail, !isOpen)
			}
			this.summaryHandlers.set(detail, handler)
			summary.addEventListener('click', handler)
		})
	}

	private setOpen(detail: HTMLDetailsElement, expand: boolean) {
		if (expand) {
			this.details.forEach((item) => {
				if (item !== detail && (this.targetStates.get(item) ?? item.open)) {
					this.setOpen(item, false)
				}
			})
		}
		const content = detail.querySelector<HTMLElement>(
			'.accordion__content'
		)
		if (!content) return

		this.targetStates.set(detail, expand)
		const startHeight = content.getBoundingClientRect().height
		this.animations.get(detail)?.cancel()
		this.animations.delete(detail)
		if (expand) detail.open = true

		if (!motionEnabled()) {
			content.style.height = expand ? 'auto' : '0px'
			if (!expand) detail.open = false
			return
		}

		content.style.height = `${startHeight}px`
		const endHeight = expand ? content.scrollHeight : 0
		const animation = content.animate(
			[
				{ height: `${startHeight}px` },
				{ height: `${endHeight}px` },
			],
			{
				duration: expand ? 240 : 180,
				easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
			}
		)
		this.animations.set(detail, animation)
		animation.onfinish = () => {
			if (this.targetStates.get(detail) !== expand) return
			this.animations.delete(detail)
			content.style.height = expand ? 'auto' : '0px'
			if (!expand) detail.open = false
		}
	}

	override disconnectedCallback(): void {
		this.summaryHandlers.forEach((handler, detail) => {
			detail.querySelector('summary')?.removeEventListener('click', handler)
		})
		this.summaryHandlers.clear()
		this.animations.forEach((animation) => animation.cancel())
		this.animations.clear()
		this.targetStates.clear()
		this.details = []
		super.disconnectedCallback()
	}
}
